import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { BarsIcon } from '@mentimeter/ragnar-visuals';
import { VisualizationTextMarkupRenderer } from '@mentimeter/tiptap/visualization-text-markup-renderer';
import { scaleBand } from 'd3-scale';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { Legend } from '../../components/Legend';
import { TextFitGhost } from '../../components/text-fit-ghost/TextFitGhost';
import type { Segment } from '../../types';
import { VISUALIZATION_LABEL_FONTSIZES } from '../../utils/constants';
import { getLongestLabel } from '../../utils/get-longest-label';

const SegmentBadge = () => {
  const t = useTranslations('presenting');
  return (
    <Box
      bg="neutralWeak"
      px="space4"
      py="space2"
      gap="space2"
      flexDirection="row"
      alignItems="center"
      borderRadius="full"
      mb="space8"
    >
      <BarsIcon />
      <Text fontWeight="semiBold" color="text">
        {t('segmentation_legend_segment_badge_label')}
      </Text>
    </Box>
  );
};

interface LegendSegment extends Segment {
  color: string;
  opacity: number;
}
interface SegmentsLegendProps extends BoxT {
  sourceQuestionTitle: string;
  segments: LegendSegment[];
  onSegmentHover: (segmentKey: Segment['position'] | null) => void;
  segmentKeyAccessor: (d: Segment) => number;
  maxHeight: number;
}

const TITLE_AND_BADGE_MAX_HEIGHT = 245;

export const SegmentsLegend = ({
  sourceQuestionTitle,
  segments,
  segmentKeyAccessor,
  onSegmentHover,
  maxHeight,
  ...rest
}: SegmentsLegendProps) => {
  const t = useTranslations('presenting');
  const labelsContainerRef = useRef<HTMLDivElement | null>(null);
  const segmentYScale = scaleBand()
    .domain(segments.map(segmentKeyAccessor).map(String))
    .range([0, maxHeight - TITLE_AND_BADGE_MAX_HEIGHT]);
  const legendItemHeight = segmentYScale.bandwidth();
  const longestLabelId = getLongestLabel(
    segments,
    (d) => d.label,
    (d) => d.id?.toString() ?? '',
  );
  const hasLabelsToResize = longestLabelId !== null;

  return (
    <Box justifyContent="center" mx="space14" flex="1" {...rest}>
      <SegmentBadge />
      <Text as="h2" fontSize="150" color="textWeak" pb="space10">
        {sourceQuestionTitle}
      </Text>
      <Box
        ref={labelsContainerRef}
        maxWidth="100%"
        style={{
          opacity: hasLabelsToResize ? 0 : 1,
          transition: 'opacity 0.5s ease',
        }}
      >
        <Legend width="100%">
          {segments.map((segment) => {
            const isLongestLabel = segment.id?.toString() === longestLabelId;
            const isUnknownSegment = segment.id === null;
            const label = isUnknownSegment
              ? t('segmentation_legend_unknown_segment_label')
              : segment.label;

            return (
              <Legend.Item
                key={segment.position}
                opacity={segment.opacity}
                onMouseEnter={() => onSegmentHover(segmentKeyAccessor(segment))}
                onMouseLeave={() => onSegmentHover(null)}
                alignItems="center"
              >
                <Legend.ItemMarker color={segment.color} />
                <Box
                  alignItems="flex-start"
                  justifyContent="flex-start"
                  style={{
                    flexShrink: 1, // Prevents the legend item from overflowing the container
                    maxWidth: '100%',
                  }}
                >
                  {isLongestLabel && (
                    <TextFitGhost
                      width="100%"
                      height={legendItemHeight}
                      text={label}
                      finalFontSizeContainerRef={labelsContainerRef}
                      fontSizes={VISUALIZATION_LABEL_FONTSIZES}
                    >
                      {(ref) => (
                        <Text aria-hidden="true" ref={ref}>
                          {label}
                        </Text>
                      )}
                    </TextFitGhost>
                  )}
                  <Legend.ItemLabel>
                    <VisualizationTextMarkupRenderer content={label} />
                  </Legend.ItemLabel>
                </Box>
              </Legend.Item>
            );
          })}
        </Legend>
      </Box>
    </Box>
  );
};
